import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './RegisterPage.module.css';
import navStyles from '../../components/layouts/PublicLayout.module.css';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const [tiers, setTiers] = useState([]);

  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await api.get("/membership-tiers");
        setTiers(res?.tiers || []);
      } catch (err) {
        // Silently fail — tiers are informational only
      }
    };
    fetchTiers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '').slice(0, 10);
      let formatted = cleaned;
      if (cleaned.length >= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      } else if (cleaned.length >= 3) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      }
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      await api.post('/inquiry', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim()
      });

      setShowModal(true);
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || 'Something went wrong. Please try again.';
      setErrors({ general: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents) => {
    const dollars = cents / 100;
    return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  };

  return (
    <div className={styles.page}>
      {/* Floating Navbar */}
      <nav className={`${navStyles.navbar} ${scrolled ? navStyles.navbarScrolled : ''}`}>
        <Link to="/" className={navStyles.logo}>
          <img loading="lazy" src="/oval-logo.webp" alt="OVAL" className={navStyles.logoImg} />
        </Link>
        <div className={navStyles.navActions}>
          {user ? (
            <Link to="/dashboard" className={navStyles.signUpBtn}>Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className={navStyles.signInBtn}>Member Login</Link>
              <Link to="/register" className={navStyles.signUpBtn}>Inquire</Link>
            </>
          )}
        </div>
      </nav>

      {/* Success Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalIcon}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#f0fdf4"/>
                <circle cx="32" cy="32" r="24" fill="#dcfce7"/>
                <path d="M24 32L29 37L40 26" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className={styles.modalTitle}>Thank You for Your Interest!</h2>
            <p className={styles.modalText}>
              A representative will reach out to you shortly to discuss membership options and answer any questions you may have.
            </p>
            <Link to="/" className={styles.modalButton}>
              Back to Home
            </Link>
          </div>
        </div>
      )}

      <div className={styles.container}>
        {/* Membership Tiers Display */}
        {tiers.length > 0 && (
          <div className={styles.card} style={{ marginBottom: '1.5rem' }}>
            <div className={styles.header}>
              <h1 style={{ fontSize: '1.35rem' }}>Membership Tiers</h1>
              <p>Explore our plans and find the right fit for you.</p>
            </div>
            <div className={styles.tiersGrid}>
              {tiers.map((tier, idx) => (
                <div key={tier.id} className={styles.tierCard} style={{ cursor: 'default' }}>
                  {idx === tiers.length - 1 && (
                    <span className={styles.tierBadge}>Most Popular</span>
                  )}
                  <span className={styles.tierName}>{tier.name}</span>
                  <span className={styles.tierPrice}>{formatPrice(tier.price)}<span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#737373' }}>/mo</span></span>
                  {tier.features && tier.features.length > 0 && (
                    <ul className={styles.tierFeatures}>
                      {tier.features.map(f => (
                        <li key={f.id} className={f.isIncluded ? styles.tierFeatureIncluded : styles.tierFeatureExcluded}>
                          <span className={styles.tierFeatureIcon}>{f.isIncluded ? '\u2713' : '\u2717'}</span>
                          {f.featureName}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* See Treatment Catalogue Button */}
        <div className={styles.catalogBtnWrap}>
          <Link to="/treatments" className={styles.catalogBtn}>
            See Treatment Catalogue
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className={styles.card}>
          <div className={styles.header}>
            <h1>Inquire About Becoming a Member</h1>
            <p>Fill out the form below and a representative will reach out to you.</p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            {errors.general && (
              <div className={styles.errorBanner}>{errors.general}</div>
            )}

            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label className={styles.label}>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
                />
                {errors.firstName && <span className={styles.fieldError}>{errors.firstName}</span>}
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                  className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
                />
                {errors.lastName && <span className={styles.fieldError}>{errors.lastName}</span>}
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              />
              {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                Phone Number <span className={styles.optional}>(optional)</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className={styles.input}
              />
            </div>

            <button type="submit" className={styles.submitBtn} disabled={loading}>
              {loading ? (
                <span className={styles.spinner}></span>
              ) : (
                'Inquire'
              )}
            </button>
          </form>

          <div className={styles.footer}>
            <p>
              Already a member?{' '}
              <Link to="/login">Sign in</Link>
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
              Or email us at <a href="mailto:support@ovalcare.com" style={{ color: '#f97316', textDecoration: 'none', fontWeight: 600 }}>support@ovalcare.com</a>
            </p>
          </div>
        </div>

        <div className={styles.features}>
          <div className={styles.feature}>
            <span className={styles.featureIcon}>&#x1F6A8;</span>
            <span>Licensed Providers</span>
          </div>
          <div className={styles.feature}>
            <span className={styles.featureIcon}>&#x1F48A;</span>
            <span>FDA-Regulated</span>
          </div>
          <div className={styles.feature}>
            <span className={styles.featureIcon}>&#x1F512;</span>
            <span>100% Confidential</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
